<template>
  <div class="rich-text-editor-container">
    <div ref="editor" class="rich-text-editor"/>
    <image-uploader
      ref="imageUploader"
      imageType="rte"
      :width="800"
      :height="400"
      shape="square"
      uploadUrl="/upload/pitch"
      :resourceId="eventId"
      :enableResize="true"
      @uploaded="onImageUpload">
    </image-uploader>
  </div>
</template>

<script>
import pell from 'pell';
import { mapState } from 'vuex';
import ImageUploader from '@/components/ImageUploader.vue';

export default {
  name: 'RichTextEditor',
  components: {
    ImageUploader,
  },
  data() {
    return {
      editor: null, // the pell instance
    };
  },
  props: {
    bus: {
      type: Object,
      default: null,
    },
    eventId: {
      type: String,
    },
  },
  mounted() {
    const { editor } = this.$refs;
    // Commented out for now
    // override paste functionality to allow only plain text
    // const pasteHandler = (e) => {
    //   e.preventDefault();
    //   const text = (e.originalEvent || e).clipboardData.getData('text/plain');
    //   document.execCommand('insertHTML', false, text);
    // };
    // editor.addEventListener('paste', pasteHandler);

    this.initPell();
    this.bus.$on('insertHTML', (content) => {
      editor.querySelector('.pell-content').focus();
      pell.exec('insertHTML', content);
    });

    this.$once('hook:beforeDestroy', () => {
      // Commented out for now
      // editor.removeEventListener('paste', pasteHandler);
      this.bus.$off('insertHTML');
    });
    this.$emit('ready');
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
  },
  methods: {
    initPell() {
      this.editor = pell.init({
        element: this.$refs.editor,
        onChange: html => this.$emit('change', html),
        actions: [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'heading1',
          'heading2', {
            name: 'heading3',
            icon: '<b>H<sub>3</sub></b>',
            title: 'Heading 3',
            result: () => pell.exec('formatBlock', 'H3'),
          }, {
            name: 'heading4',
            icon: '<b>H<sub>4</sub></b>',
            title: 'Heading 4',
            result: () => pell.exec('formatBlock', 'H4'),
          },
          'paragraph', {
            name: 'removeFormat',
            icon: '<b><i>T</i><sub>x</sub></b>',
            title: 'Remove Formatting',
            result: () => {
              pell.exec('removeFormat');
              pell.exec('fontSize', 3);
            },
          }, {
            name: 'orderedList',
            icon: '<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
            title: 'Ordered List',
            result: () => pell.exec('insertOrderedList'),
          }, {
            name: 'unorderedList',
            icon: '<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/><path fill="none" d="M0 0h24v24H0V0z"/></svg>',
            title: 'Unordered List',
            result: () => pell.exec('insertUnorderedList'),
          }, {
            name: 'justifyLeft',
            icon: '<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
            title: 'Justify Left',
            result: () => pell.exec('justifyLeft'),
          }, {
            name: 'justifyCenter',
            icon: '<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
            title: 'Justify Center',
            result: () => pell.exec('justifyCenter'),
          }, {
            name: 'justifyRight',
            icon: '<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
            title: 'Justify Right',
            result: () => pell.exec('justifyRight'),
          }, {
            name: 'justifyFull',
            icon: '<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
            title: 'Justify Full',
            result: () => pell.exec('justifyFull'),
          },
          'line',
          'link', {
            name: 'croppieImage',
            icon: '&#128247;',
            title: 'Image',
            result: () => {
              this.$refs.imageUploader.$refs.file.click();
            },
          }, {
            name: 'video',
            icon: '&#128249;',
            title: 'Video',
            result: async () => {
              try {
                // eslint-disable-next-line
                const url = window.prompt('Enter a YouTube of Vimeo URL');
                if (!url) {
                  return;
                }
                const response = await this.backendAxios.get(`/oembed?url=${url}`);
                const embedURL = decodeURI(response.data);
                pell.exec('insertHTML', `<p class="videoWrapper">${embedURL}</p>`);
              } catch (err) {
                this.$toasted.show(err.response.data, {
                  type: 'info',
                  duration: 5000,
                });
              }
            },
          },
        ],
      });
    },
    onImageUpload(url) {
      pell.exec(
        'insertHTML',
        `<img alt="youpaired" src="${url}" style="width: 100%;" />`,
      );
    },
  },
};
</script>

<style lang="scss">
$pell-content-height: 400px;
@import '@/assets/youpaired-colors.scss';
@import '../../node_modules/pell/src/pell';

.rich-text-editor-container {
  background-color: white;
  text-align: left;
  border: 1px solid #DCDBDB;
  font-size: 1rem;
  color:#444444;
  position: relative;
  // overflow: auto;
  display: flex;
  width: 100%;
  margin: 0 auto;
}

.rich-text-editor-container .pell-content {
  min-height: 20rem;
  height: auto;
}
.rich-text-editor-container .pell-actionbar {
  padding: 1rem 0;
  display: flex;
  align-items: center;
  position: sticky;
  top: 4rem;
  flex-wrap: wrap;
  z-index: 10;
}

.rich-text-editor-container .pell-button {
  padding: 0 1.25rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: $login-background;
  }
}

.rich-text-editor-container .pell-button svg {
  overflow: unset;
}

.rich-text-editor {
  width: 100%;
}

</style>
